import CheckIcon from '@mui/icons-material/Check';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api';
import { formatCurrencyInCents } from '../../helpers/formatters';
import {
    formatPlanLimit,
    isUnlimitedLimit,
    planLimitFor,
    planQuotaPeriodLabel,
    PLAN_QUOTAS,
} from '../../helpers/planLimits';
import {
    dispatchRegistrationPlanSelected,
    planRegistrationActionLabel,
} from '../../helpers/publicRegistrationPlan';
import { canonicalPlanName, hasProAccess, publicPlanDisplayName } from '../../helpers/subscription';
import { ProFarmLinkGate } from '../subscription/subscriptionPlanMigration';
import { publicCardSx, publicColors, publicContainerSx, publicH2Sx, publicSectionSx, publicSubtitleSx } from './publicTheme';

function formatPublicPlanPrice(monthlyBilledValue) {
    const cents = Number(monthlyBilledValue) || 0;
    if (cents <= 0) {
        return '0,00';
    }
    return formatCurrencyInCents(cents).replace(/^R\$\s*/, '');
}

function mapPlanFromApi(plan) {
    const cents = Number(plan?.monthly_billed_value) || 0;
    const features = Array.isArray(plan?.features)
        ? plan.features.filter((item) => String(item || '').trim())
        : [];

    return {
        id: plan?.uuid || plan?.id || '',
        rawName: plan?.name || '',
        name: publicPlanDisplayName(plan?.name),
        price: formatPublicPlanPrice(cents),
        pain: String(plan?.description || '').trim(),
        features,
        monthlyBilledValue: cents,
        isFree: cents <= 0,
        isPro: hasProAccess(plan?.name),
        // O Pro é a opção que queremos que o visitante escolha; o Unlimited existe
        // acima dele apenas como ancoragem.
        isRecommended: canonicalPlanName(plan?.name) === 'Pro',
        limits: plan?.limits || {},
        hasLimits: planHasAnyLimit(plan),
    };
}

function planHasAnyLimit(plan) {
    return PLAN_QUOTAS.some((quota) => !isUnlimitedLimit(planLimitFor(plan, quota.key)));
}

function isInheritedPlanFeature(feature) {
    return /^tudo do plano\b/i.test(String(feature || '').trim());
}

function buildEffectiveFeaturesByPlan(plans) {
    const sorted = [...plans].sort((a, b) => a.monthlyBilledValue - b.monthlyBilledValue);
    const effectiveByPlanId = new Map();
    const accumulated = new Set();

    sorted.forEach((plan) => {
        plan.features.forEach((feature) => {
            if (!isInheritedPlanFeature(feature)) {
                accumulated.add(feature);
            }
        });
        effectiveByPlanId.set(plan.id, new Set(accumulated));
    });

    return { effectiveByPlanId, featureSet: accumulated };
}

function buildFeatureRows(plans) {
    const { effectiveByPlanId, featureSet } = buildEffectiveFeaturesByPlan(plans);

    return Array.from(featureSet).map((feature) => [
        feature,
        ...plans.map((plan) => {
            const effective = effectiveByPlanId.get(plan.id);
            return effective?.has(feature) ? '✔' : '—';
        }),
    ]);
}

function buildLimitRows(plans) {
    return PLAN_QUOTAS.map((quota) => [
        `${quota.label} (${planQuotaPeriodLabel(quota.period)})`,
        ...plans.map((plan) => formatPlanLimit(planLimitFor(plan, quota.key))),
    ]);
}

/**
 * Deixa explícito que "ilimitado" é ausência de limite de quantidade na conta, e
 * não promessa de capacidade infinita de infraestrutura, para não configurar
 * publicidade enganosa.
 */
function UnlimitedCapacityDisclaimer() {
    return (
        <Box sx={{ mt: 3, maxWidth: 900, mx: 'auto' }}>
            <Typography
                sx={{
                    color: publicColors.inkMuted,
                    fontSize: '0.78rem',
                    lineHeight: 1.7,
                }}
            >
                <Box component="span" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    Sobre os limites e a capacidade da plataforma
                </Box>
                Onde consta <strong>Ilimitado</strong>, significa que não aplicamos nenhum limite de
                quantidade à sua conta: você cadastra quantas impressoras, vendas, impressões,
                filamentos e demais registros precisar, sem bloqueio por volume. Esse compromisso é
                sobre <strong>limitação de uso</strong>, e não sobre capacidade de infraestrutura.
                A plataforma funciona em servidores com processamento, memória e armazenamento
                finitos e compartilhados entre os assinantes. Em cenários de volume muito acima do
                normal, ou em momentos de pico, pode haver alguma oscilação de desempenho, como um
                tempo maior para gerar relatórios e importações. Se isso acontecer, o dimensionamento
                dos recursos computacionais necessários será conduzido com a maior celeridade
                possível. Vale considerar que
                ampliar infraestrutura envolve provisionamento e ajustes, portanto{' '}
                <strong>não é instantâneo</strong>. Monitoramos a plataforma continuamente e agimos o
                quanto antes, mas não garantimos prazo de atendimento, tempo de resposta, velocidade
                de processamento nem disponibilidade ininterrupta, salvo se acordado por escrito em
                contrato específico. Se você já opera, ou pretende operar, em volume muito elevado,
                fale com a gente antes de assinar para avaliarmos juntos o dimensionamento adequado
                à sua operação.
            </Typography>
        </Box>
    );
}

const PlanComparisonTable = React.forwardRef(({ plans, rows, firstColumnLabel }, ref) => (
    <TableContainer
        ref={ref}
        sx={{
            mt: 3,
            border: `1px solid ${publicColors.border}`,
            borderRadius: 2,
            bgcolor: publicColors.surface,
            overflowX: 'auto',
        }}
    >
        <Table size="small">
            <TableHead>
                <TableRow sx={{ bgcolor: publicColors.surfaceAlt }}>
                    <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>{firstColumnLabel}</TableCell>
                    {plans.map((plan) => (
                        <TableCell
                            key={plan.id}
                            align="center"
                            sx={{
                                fontWeight: 700,
                                minWidth: 120,
                                ...(plan.isRecommended ? { color: publicColors.primary } : {}),
                            }}
                        >
                            {plan.name}
                            {plan.isRecommended ? (
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                    Melhor opção
                                </Typography>
                            ) : null}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(([label, ...cols]) => (
                    <TableRow key={label}>
                        <TableCell>{label}</TableCell>
                        {cols.map((cell, index) => (
                            <TableCell key={`${label}-${plans[index]?.id || index}`} align="center">
                                {cell}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
));

const Pricing = () => {
    const [isTableVisible, setIsTableVisible] = useState(false);
    const [isLimitsTableVisible, setIsLimitsTableVisible] = useState(false);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [pendingPlan, setPendingPlan] = useState(null);
    const [showFarmLinkGate, setShowFarmLinkGate] = useState(false);
    const tableRef = useRef(null);
    const limitsTableRef = useRef(null);
    const farmLinkGateRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        const loadPlans = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await api.get('/plans');
                const items = response?.data?.data || [];
                const mapped = (Array.isArray(items) ? items : [])
                    .map(mapPlanFromApi)
                    .filter((plan) => plan.id);

                if (!cancelled) {
                    setPlans(mapped);
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar os planos no momento.');
                    setPlans([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadPlans();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!showFarmLinkGate) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            const target = farmLinkGateRef.current;
            const headerHeight = document.querySelector('header')?.offsetHeight || 72;
            if (target) {
                window.scrollTo({ top: target.offsetTop - headerHeight, behavior: 'smooth' });
            }
        }, 280);
        return () => window.clearTimeout(timer);
    }, [showFarmLinkGate]);

    const featureRows = useMemo(() => buildFeatureRows(plans), [plans]);
    const limitRows = useMemo(() => buildLimitRows(plans), [plans]);
    // Com o Unlimited passamos a ter 4 planos, que não cabem em colunas de 4/12.
    const planColumnWidth = plans.length >= 4 ? 3 : 4;

    const showUsageLimits = () => {
        setIsLimitsTableVisible(true);
        window.setTimeout(() => {
            const target = limitsTableRef.current;
            const headerHeight = document.querySelector('header')?.offsetHeight || 72;
            if (target) {
                window.scrollTo({ top: target.offsetTop - headerHeight - 16, behavior: 'smooth' });
            }
        }, 320);
    };

    const scrollToRegister = () => {
        const target = document.getElementById('registrationForm');
        const headerHeight = document.querySelector('header')?.offsetHeight || 72;
        if (target) {
            window.scrollTo({ top: target.offsetTop - headerHeight, behavior: 'smooth' });
        }
    };

    const finalizePlanSelection = (plan, { scroll = true } = {}) => {
        setSelectedPlanId(plan.id);
        setShowFarmLinkGate(false);
        setPendingPlan(null);
        dispatchRegistrationPlanSelected(plan);
        if (scroll) {
            scrollToRegister();
        }
    };

    const handleSelectPlan = (plan) => {
        if (plan.isPro) {
            setSelectedPlanId(null);
            setPendingPlan(plan);
            setShowFarmLinkGate(true);
            return;
        }
        setShowFarmLinkGate(false);
        setPendingPlan(null);
        finalizePlanSelection(plan);
    };

    const handleFarmLinkBack = () => {
        setShowFarmLinkGate(false);
        setPendingPlan(null);
    };

    const activePlanId = pendingPlan?.id || selectedPlanId;

    return (
        <Box id="precos" component="section" sx={{ ...publicSectionSx, bgcolor: publicColors.surface }}>
            <Box sx={publicContainerSx}>
                <Typography component="h2" sx={{ ...publicH2Sx, textAlign: 'center' }}>
                    Planos para cada estágio do seu negócio
                </Typography>
                <Typography sx={{ ...publicSubtitleSx, textAlign: 'center', mb: 5 }}>
                    Comece sem custo. Evolua quando precisar.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {!loading && error ? (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                ) : null}

                {!loading && !error && plans.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: publicColors.inkMuted, mb: 3 }}>
                        Nenhum plano disponível no momento.
                    </Typography>
                ) : null}

                {!loading && plans.length > 0 ? (
                    <Grid container spacing={3} justifyContent="center">
                        {plans.map((plan) => {
                            const isHighlighted = activePlanId ? plan.id === activePlanId : plan.isRecommended;
                            return (
                                <Grid item xs={12} sm={6} md={planColumnWidth} key={plan.id}>
                                    <Box
                                        sx={{
                                            ...publicCardSx,
                                            position: 'relative',
                                            borderColor: isHighlighted ? publicColors.primary : publicColors.border,
                                            borderWidth: isHighlighted ? 2 : 1,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            ...(plan.isRecommended
                                                ? {
                                                    boxShadow: `0 12px 32px ${alpha(publicColors.primary, 0.18)}`,
                                                    transform: { md: 'scale(1.04)' },
                                                }
                                                : {}),
                                        }}
                                    >
                                        {plan.isRecommended ? (
                                            <Chip
                                                label="Melhor opção"
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    bgcolor: publicColors.primary,
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    letterSpacing: 0.4,
                                                }}
                                            />
                                        ) : null}
                                        <Typography variant="overline" sx={{ color: publicColors.inkSoft, fontWeight: 600 }}>
                                            {plan.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: publicColors.ink, my: 1 }}>
                                            R$ {plan.price}
                                            <Typography component="span" variant="body2" sx={{ color: publicColors.inkSoft }}>
                                                {' '}
                                                /mês
                                            </Typography>
                                        </Typography>
                                        {plan.pain ? (
                                            <Typography variant="body2" sx={{ color: publicColors.inkMuted, mb: 2, minHeight: 48 }}>
                                                {plan.pain}
                                            </Typography>
                                        ) : null}
                                        <List dense disablePadding sx={{ flex: 1 }}>
                                            {plan.features.map((feature) => (
                                                <ListItem key={`${plan.id}-${feature}`} disableGutters sx={{ py: 0.25 }}>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <CheckIcon sx={{ fontSize: 20, color: publicColors.accent }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Button
                                            fullWidth
                                            variant={isHighlighted ? 'contained' : 'outlined'}
                                            onClick={() => handleSelectPlan(plan)}
                                            sx={{
                                                mt: 3,
                                                fontWeight: 600,
                                                boxShadow: 'none',
                                                ...(isHighlighted
                                                    ? {
                                                        bgcolor: publicColors.primary,
                                                        '&:hover': { bgcolor: publicColors.primaryDark, boxShadow: 'none' },
                                                    }
                                                    : {
                                                        borderColor: publicColors.primary,
                                                        color: publicColors.primary,
                                                        '&:hover': {
                                                            borderColor: publicColors.primaryDark,
                                                            bgcolor: publicColors.primarySoft,
                                                        },
                                                    }),
                                            }}
                                        >
                                            {planRegistrationActionLabel(plan)}
                                        </Button>
                                        {plan.hasLimits ? (
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={showUsageLimits}
                                                sx={{
                                                    mt: 1.5,
                                                    alignSelf: 'center',
                                                    color: publicColors.inkSoft,
                                                    fontSize: '0.75rem',
                                                    textDecorationColor: 'currentColor',
                                                }}
                                            >
                                                * ver limites de uso
                                            </Link>
                                        ) : null}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : null}

                {showFarmLinkGate && pendingPlan ? (
                    <Box ref={farmLinkGateRef} sx={{ mt: 4, scrollMarginTop: 24 }}>
                        <ProFarmLinkGate
                            intent="registration"
                            onBack={handleFarmLinkBack}
                            onProceed={() => finalizePlanSelection(pendingPlan)}
                        />
                    </Box>
                ) : null}

                {!loading && plans.length > 0 && featureRows.length > 0 ? (
                    <>
                        <Box textAlign="center" sx={{ mt: 4 }}>
                            <Button
                                variant="text"
                                onClick={() => setIsTableVisible(!isTableVisible)}
                                sx={{ color: publicColors.primary, fontWeight: 600 }}
                            >
                                {isTableVisible ? 'Ocultar comparativo' : 'Ver comparativo de recursos'}
                            </Button>
                        </Box>
                        <Collapse in={isTableVisible}>
                            <PlanComparisonTable
                                ref={tableRef}
                                plans={plans}
                                rows={featureRows}
                                firstColumnLabel="Recurso"
                            />
                        </Collapse>
                    </>
                ) : null}

                {!loading && plans.length > 0 && limitRows.length > 0 ? (
                    <>
                        <Box textAlign="center" sx={{ mt: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => setIsLimitsTableVisible(!isLimitsTableVisible)}
                                sx={{ color: publicColors.primary, fontWeight: 600 }}
                            >
                                {isLimitsTableVisible ? 'Ocultar limites de uso' : 'Ver limites de uso'}
                            </Button>
                        </Box>
                        <Collapse in={isLimitsTableVisible}>
                            <Typography variant="body2" sx={{ color: publicColors.inkMuted, mt: 3, textAlign: 'center' }}>
                                Os limites mensais reiniciam no 1º dia de cada mês. Impressoras, produtos e parceiros
                                contam o total cadastrado na conta.
                            </Typography>
                            <PlanComparisonTable
                                ref={limitsTableRef}
                                plans={plans}
                                rows={limitRows}
                                firstColumnLabel="Limite"
                            />
                            <UnlimitedCapacityDisclaimer />
                        </Collapse>
                    </>
                ) : null}

                <Box
                    sx={{
                        mt: 5,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: publicColors.surfaceAlt,
                        border: `1px solid ${publicColors.border}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ fontWeight: 600, color: publicColors.ink, mb: 1 }}>
                        Suporte humano por e-mail e WhatsApp
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default Pricing;
